import { trpc } from '../../../lib/trpc';
import type { ReviewType } from '@wineryxplorer/shared';

export interface ModerationOutcome {
  _id: string;
  reviewId: string;
  reviewType: ReviewType;
  flagType: 'venueManager' | 'community';
  decision: 'approved' | 'removed';
  reason: string;
  decidedAt: Date | string;
}

export interface ModerationOutcomesProps {
  className?: string;
}

// Helper to format date
const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Get review type label
const getReviewTypeLabel = (type: ReviewType): string => {
  const labels: Record<ReviewType, string> = {
    venue: 'Venue',
    event: 'Event',
    experience: 'Experience',
    wine: 'Wine',
  };
  return labels[type] || type;
};

// Get flag type label
const getFlagTypeLabel = (flagType: 'venueManager' | 'community'): string => {
  return flagType === 'venueManager' ? 'Venue Manager Flag' : 'Community Flag';
};

// Individual outcome card
const OutcomeCard = ({ outcome }: { outcome: ModerationOutcome }) => {
  const isApproved = outcome.decision === 'approved';

  return (
    <article
      className={`border rounded-lg p-4 ${
        isApproved
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            isApproved ? 'bg-green-100' : 'bg-red-100'
          }`}
        >
          {isApproved ? (
            <svg
              className="w-5 h-5 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 text-red-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span
              className={`font-medium ${
                isApproved ? 'text-green-800' : 'text-red-800'
              }`}
            >
              {isApproved ? 'Review Approved' : 'Review Removed'}
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-sm text-gray-600">
              {getReviewTypeLabel(outcome.reviewType)} Review
            </span>
          </div>

          {/* Flag type badge */}
          <div className="mb-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                outcome.flagType === 'venueManager'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-orange-100 text-orange-700'
              }`}
            >
              {getFlagTypeLabel(outcome.flagType)}
            </span>
          </div>

          {/* Reason */}
          <p
            className={`text-sm ${
              isApproved ? 'text-green-700' : 'text-red-700'
            }`}
          >
            <span className="font-medium">Reason:</span> {outcome.reason}
          </p>

          {/* Date */}
          <p className="text-xs text-gray-500 mt-2">
            Decided on {formatDate(outcome.decidedAt)}
          </p>
        </div>
      </div>
    </article>
  );
};

export const ModerationOutcomes = ({ className = '' }: ModerationOutcomesProps) => {
  const { data, isLoading, error } = trpc.moderation.venueAdminOutcomes.useQuery();

  if (isLoading) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="inline-flex items-center gap-2 text-gray-500">
          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Loading moderation outcomes...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="inline-flex items-center gap-2 text-red-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Error loading outcomes: {error.message}
        </div>
      </div>
    );
  }

  const outcomes = (data?.items || []) as ModerationOutcome[];

  if (outcomes.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No moderation outcomes</h3>
        <p className="mt-1 text-sm text-gray-500">
          You haven't flagged any reviews yet, or no decisions have been made on your flags.
        </p>
      </div>
    );
  }

  // Count approved vs removed
  const approvedCount = outcomes.filter((o) => o.decision === 'approved').length;
  const removedCount = outcomes.filter((o) => o.decision === 'removed').length;

  return (
    <div className={className}>
      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-700">{approvedCount}</div>
          <div className="text-sm text-green-600">Reviews Approved</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-700">{removedCount}</div>
          <div className="text-sm text-red-600">Reviews Removed</div>
        </div>
      </div>

      {/* Outcomes list */}
      <div className="space-y-4">
        {outcomes.map((outcome) => (
          <OutcomeCard key={outcome._id} outcome={outcome} />
        ))}
      </div>

      {/* Total count */}
      <div className="mt-4 text-sm text-gray-500 text-center">
        Showing {outcomes.length} moderation outcome{outcomes.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};
